import { createClient } from '@supabase/supabase-js';
import { useState, useEffect, createContext, useContext } from 'react';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const validateAndGetSession = async () => {
      try {
        // First get the locally cached session
        const { data: { session: cachedSession } } = await supabase.auth.getSession();

        if (cachedSession) {
          // Verify the session is still valid on the server by calling getUser()
          // This makes an API call to Supabase to validate the token
          const { data: { user }, error } = await supabase.auth.getUser();

          if (error || !user) {
            // Session is invalid on server - user was logged out from another device
            console.log('Session invalid on server, signing out locally');
            await supabase.auth.signOut();
            setSession(null);
            localStorage.removeItem('ApiSessionID');
          } else {
            // Session is valid
            setSession(cachedSession);
          }
        } else {
          setSession(null);
        }
      } catch (error) {
        console.error('Error validating session:', error);
        setSession(null);
      }
      setIsLoading(false);
    };

    validateAndGetSession();

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setIsLoading(false);

      // Handle password recovery - redirect to reset password page
      if (event === 'PASSWORD_RECOVERY') {
        if (window.location.pathname !== '/reset-password') {
          window.location.href = '/reset-password';
        }
      }

      // Handle logout from another browser/tab - redirect to login
      if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
        // Clear any local storage/cookies that might be stale
        localStorage.removeItem('ApiSessionID');
        // Redirect to login page
        const publicPaths = ['/login', '/privacy-policy', '/forgot-password', '/reset-password'];
        if (!publicPaths.includes(window.location.pathname)) {
          window.location.href = '/login';
        }
      }
    });

    return () => {
      subscription?.unsubscribe?.();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ session, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
